/**
 * emailAttachmentCard — WHAT THE STAGED EMAIL CARD SAYS ABOUT FILES (2026-09-17,
 * client twin).
 *
 * Miles's fifth ask on the "ready to send — your call" element was attachment
 * ability. Two facts from the live catalog shape everything here, and both were
 * READ rather than assumed (`ops-diagnose-composio.yml dump_tool_schemas`,
 * runs 35231318782 / 35232177124):
 *
 *   • the `attachment` parameter is ONE object on every send tool both
 *     providers have, `additionalProperties: false` — one file per email is a
 *     platform ceiling, not a product decision;
 *   • so when several files are named, the first attaches and the rest ride the
 *     body as links (Miles's call, 2026-09-17) — which the card must SAY, not
 *     silently do.
 *
 * Hence this module: the vocabulary for "here is what is going with this email,
 * and here is what is not, and why." Two shapes with one rule between them —
 * every named file appears in exactly one of them, so a person reading the card
 * can never be missing a file they mentioned.
 *
 * THE DISPLAY/LOCATOR SPLIT IS LOAD-BEARING. These rows are the card's, and the
 * card is served to every participant in the thread: they carry a name, a type,
 * a size and a source, and NEVER a storage path. The locators that say which
 * bytes to fetch live in the held action's `params`, server-side, and are
 * re-authorized against whoever decides the send. A path in the preview would
 * be a read capability handed to whoever can see the card.
 *
 * The words are code-authored on purpose (D9, class `state`): they are facts
 * about a file on a person's own card — "too big to attach" — not a sentence in
 * Prest0n's voice, and the model must not be able to phrase away the reason a
 * contract is not going out.
 *
 * VERBATIM TWIN of functions/src/prest0nSession/emailAttachmentCard.ts
 * (functions/ and src/ are separate tsconfig roots and cannot import each
 * other — the messageAuthorship.ts idiom). Behavioural parity is pinned by
 * tests/prest0nSession/emailAttachmentCard.parity.test.ts.
 *
 * PURE: no React, no Firebase, no clock — so the chat tile can import it
 * without dragging firebase/auth into a suite's import graph.
 */

/** Where an attached file's bytes come from. Absent ⇒ `conversation`, which is
 *  every attachment written before connected-app files existed. */
export type CardAttachmentSource = 'conversation' | 'app-file';

export const CARD_ATTACHMENT_SOURCES: ReadonlyArray<CardAttachmentSource> = [
  'conversation',
  'app-file',
];

/** What the card knows about one attached file — a locator's SHADOW, never
 *  bytes and never a path. */
export interface CardAttachment {
  name: string;
  type?: string;
  size?: number;
  source?: CardAttachmentSource;
}

/**
 * Why a named file is not attached. `sent-as-link` is not a failure — it is the
 * honest half of Miles's "the first attaches, the rest become links": the file
 * IS being delivered, in the body, and the card says which way.
 */
export type CardAttachmentIssueReason =
  | 'not-found'
  | 'not-authorized'
  | 'too-large'
  | 'provider-unsupported'
  | 'sent-as-link'
  | 'link-failed'
  | 'app-file-too-large'
  | 'app-file-not-found';

export const CARD_ATTACHMENT_ISSUE_REASONS: ReadonlyArray<CardAttachmentIssueReason> = [
  'not-found',
  'not-authorized',
  'too-large',
  'provider-unsupported',
  'sent-as-link',
  'link-failed',
  'app-file-too-large',
  'app-file-not-found',
];

export interface CardAttachmentIssue {
  name: string;
  reason: CardAttachmentIssueReason;
}

/**
 * What each reason MEANS, in the two ways that matter: the words a person
 * reads, and whether the file still reaches the recipient. Declared as one
 * table so those two can never drift — a reason whose words say "sent as a
 * link" while the button treats it as a missing file is the shape of bug this
 * card exists to prevent.
 *
 * `delivered: true` is why `send it` stays enabled for an oversized file: a
 * link in the body IS a delivery. Only a file nobody can find or nobody may
 * read holds the send.
 */
export interface AttachmentIssueFact {
  words: string;
  delivered: boolean;
}

export const ATTACHMENT_ISSUE_FACTS: Record<CardAttachmentIssueReason, AttachmentIssueFact> = {
  'not-found': { words: 'not found in this conversation', delivered: false },
  'not-authorized': { words: 'not available to attach', delivered: false },
  'too-large': { words: 'too big to attach — sent as a link instead', delivered: true },
  'provider-unsupported': {
    words: 'this email account cannot attach files — sent as a link instead',
    delivered: true,
  },
  'sent-as-link': { words: 'sent as a link in the message', delivered: true },
  // The one genuinely OURS: a file we found, the person may read, and we could
  // not prepare — the signed link did not mint. It blocks the send, because the
  // alternative is an email that quietly arrives without the exhibit.
  'link-failed': { words: 'could not be prepared for this email', delivered: false },
  // A file in the connected Drive that will not fit in an email. It is NOT
  // carried as a link, and that is the one place app files differ: copying
  // somebody's Drive file into our storage purely to mint a 7-day bearer url
  // is a bigger step than they asked for, and Drive's own sharing is the right
  // tool. So this holds the send and says plainly why.
  'app-file-too-large': { words: 'too big to send from your connected files', delivered: false },
  // Its own reason rather than `not-found`, whose words are "not found in this
  // CONVERSATION" — the wrong sentence about a file that was named by its id in
  // somebody's Drive, and the kind of near-miss that sends a person looking in
  // the wrong place.
  'app-file-not-found': { words: 'not found in your connected files', delivered: false },
};

/**
 * ONE vocabulary for why a file is not going out as an attachment. Read by the
 * html card builder AND by the React staged-send tile: a second copy of these
 * words is how two surfaces start telling a person different things about the
 * same file (the threadNotifyMode idiom). Derived from the table above rather
 * than restated, and kept under its original name because both renderers
 * already import it.
 */
export const ATTACHMENT_ISSUE_WORDS: Record<CardAttachmentIssueReason, string> =
  CARD_ATTACHMENT_ISSUE_REASONS.reduce((acc, reason) => {
    acc[reason] = ATTACHMENT_ISSUE_FACTS[reason].words;
    return acc;
  }, {} as Record<CardAttachmentIssueReason, string>);

/**
 * Where a file came from, said only when it is worth saying. A file from this
 * conversation needs no provenance line — the person is looking at the
 * conversation — so the map is deliberately partial rather than carrying an
 * empty string for it.
 */
export const ATTACHMENT_SOURCE_WORDS: Partial<Record<CardAttachmentSource, string>> = {
  'app-file': 'from your connected files',
};

/**
 * How many named files one email may carry through staging. The same cap
 * `steer_branches` uses for `attachment_names`: past it, a person is not
 * attaching files, and a body of links stops being a message.
 */
export const CARD_ATTACHMENT_CAP = 8;

export function attachmentIssueWords(reason: unknown): string | null {
  const key = String(reason || '') as CardAttachmentIssueReason;
  return CARD_ATTACHMENT_ISSUE_REASONS.includes(key) ? ATTACHMENT_ISSUE_WORDS[key] : null;
}

export function attachmentSourceWords(source: unknown): string | null {
  const key = String(source || '') as CardAttachmentSource;
  if (!CARD_ATTACHMENT_SOURCES.includes(key)) return null;
  return ATTACHMENT_SOURCE_WORDS[key] || null;
}

/**
 * Validate one row. FAIL-CLOSED on anything unrecognised: a row with no name
 * says nothing useful, and an unknown `source` or a nonsense `size` would put
 * an invented fact on somebody's outgoing correspondence. Optional fields are
 * DROPPED rather than defaulted, so a row that never knew its size renders as
 * a bare name instead of "0 B".
 */
export function normalizeCardAttachment(raw: unknown): CardAttachment | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const name = String(r.name || '').trim();
  if (!name) return null;
  const out: CardAttachment = { name };
  const type = String(r.type || '').trim();
  if (type) out.type = type;
  const size = Number(r.size);
  if (Number.isFinite(size) && size > 0) out.size = Math.floor(size);
  const source = String(r.source || '') as CardAttachmentSource;
  if (CARD_ATTACHMENT_SOURCES.includes(source)) out.source = source;
  return out;
}

export function normalizeCardAttachments(raw: unknown, cap = CARD_ATTACHMENT_CAP): CardAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: CardAttachment[] = [];
  for (const row of raw) {
    const one = normalizeCardAttachment(row);
    if (one) out.push(one);
    if (out.length >= cap) break;
  }
  return out;
}

export function normalizeCardAttachmentIssue(raw: unknown): CardAttachmentIssue | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const name = String(r.name || '').trim();
  const reason = String(r.reason || '') as CardAttachmentIssueReason;
  if (!name || !CARD_ATTACHMENT_ISSUE_REASONS.includes(reason)) return null;
  return { name, reason };
}

export function normalizeCardAttachmentIssues(
  raw: unknown,
  cap = CARD_ATTACHMENT_CAP,
): CardAttachmentIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: CardAttachmentIssue[] = [];
  for (const row of raw) {
    const one = normalizeCardAttachmentIssue(row);
    if (one) out.push(one);
    if (out.length >= cap) break;
  }
  return out;
}

/**
 * Does this file reach the recipient at all? Read off the facts table, so the
 * button and the card can never disagree about it. An unrecognised reason is
 * treated as NOT delivered — the fail-closed direction, because the honest
 * answer to "is this going out?" when we do not recognise the reason is no.
 */
export function issueStillDelivers(reason: unknown): boolean {
  const key = String(reason || '') as CardAttachmentIssueReason;
  return CARD_ATTACHMENT_ISSUE_REASONS.includes(key)
    ? ATTACHMENT_ISSUE_FACTS[key].delivered
    : false;
}

/**
 * The card's own question: may this email still be sent? Every named file is
 * either attached, or carried as a link, or it is not going — and the last case
 * is the only one that holds the button. Written here rather than in either
 * renderer so the html card and the React tile ask it the same way.
 */
export function attachmentsBlockSend(issues: ReadonlyArray<CardAttachmentIssue> | undefined): boolean {
  return (issues || []).some((i) => i && !issueStillDelivers(i.reason));
}
